/* eslint-disable react-hooks/exhaustive-deps */
import {useEffect, useState} from 'react'
import {Typography} from '@mui/material'
import DialogCustom from 'src/components/elements/DialogCustom/DialogCustom'
import PrimaryButton from 'src/components/elements/Buttons/PrimaryButton/PrimaryButton'
import SecondaryButton from 'src/components/elements/Buttons/SecondaryButton/SecondaryButton'
import {STATUS} from 'src/constants/constants'
import apiClient from 'src/services/apiClient'
import {
  formatAssumptionDateTime,
  isNextDoseMoveValid,
  SecondaryAssumption
} from 'src/constants/secondaryAssumptions'
import NextDoseFields from './NextDoseFields'

// Legacy-stack twin of pages-new/secondaryAssumptions/MoveNextDoseDialogNew: moves the derived
// "next dose" row to another time without answering it (therapy-service RF4.5) — the way a patient
// with reminders off, who never gets the reminder link, can still move it.

type MoveNextDoseDialogProps = {
  open: boolean
  assumption?: SecondaryAssumption
  onClose: () => void
  // the new expectedDate, i.e. the derived row's new natural key
  onSuccess: (expectedDate: string) => void
  // a 400 almost always means the list is stale: the caller reloads it
  onError: () => void
}

const MoveNextDoseDialog = ({open, assumption, onClose, onSuccess, onError}: MoveNextDoseDialogProps) => {
  const [value, setValue] = useState<string | null>(null)
  const [statusSubmit, setStatusSubmit] = useState<string | undefined>(undefined)

  useEffect(() => {
    if (open) {
      setValue(assumption?.expectedDate ?? null)
      setStatusSubmit(undefined)
    }
  }, [open, assumption?.expectedDate])

  const isValid = isNextDoseMoveValid(value, assumption?.expectedDate)
  // an untouched value is not an error, just nothing to send yet
  const isUnchangedOrEmpty = !value || value === assumption?.expectedDate

  const onSubmit = async () => {
    if (!assumption?.expectedDate || !value) { return }
    const moved = await apiClient.moveSecondaryNextDose({
      secondaryTherapyId: assumption.secondaryTherapyId,
      // the row's own address, exactly as received (see the FE handover note)
      expectedDate: assumption.expectedDate,
      nextExpectedDate: new Date(value).toISOString()
    }, setStatusSubmit)
    if (moved?.expectedDate) {
      onSuccess(moved.expectedDate)
    } else {
      onError()
    }
  }

  return (
    <DialogCustom
      maxWidth='sm'
      onClose={onClose}
      open={open}
      title={{text: 'Sposta prossima assunzione', format: false}}
      body={assumption ? (
        <>
          <Typography variant='body2' color='textSecondary' style={{marginBottom: 24}}>
            {`${assumption.drugName ?? 'Terapia accessoria'} — assunzione prevista il ${formatAssumptionDateTime(assumption.expectedDate)}.`}
          </Typography>
          <NextDoseFields
            value={value}
            onChange={setValue}
            disabled={statusSubmit === STATUS.PENDING}
            error={!isUnchangedOrEmpty && !isValid}
          />
        </>
      ) : <></>}
      footer={
        <>
          <SecondaryButton onClick={onClose}>Annulla</SecondaryButton>
          <PrimaryButton disabled={!isValid || statusSubmit === STATUS.PENDING} onClick={onSubmit}>
            Sposta
          </PrimaryButton>
        </>
      }
    />
  )
}

export default MoveNextDoseDialog
